import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import { getJpdViewsServicePathPrefix } from '@jira/polaris__lib-jpd-views-service/src/index.tsx';
import { wrapPromiseWithFetchInteraction } from '@jira/polaris__lib-analytics/src/wrapPromiseWithFetchInteraction.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { NavigationView } from './NavigationView.types.tsx';
import { getEarlyFetchResponse } from './getEarlyFetchResponse.tsx';

export const fetchView = (viewUUID: string): Promise<NavigationView> =>
	fetchJson<NavigationView>(
		`${getJpdViewsServicePathPrefix()}/v1/views/${viewUUID}?operation=getJpdNavigationView`,
	);

const fetchViewsFactory =
	<T,>(type: 'projects' | 'collections') =>
	(id: T): Promise<NavigationView[]> =>
		fetchJson<{
			globalViews?: NavigationView[];
			views: NavigationView[];
		}>(
			`${getJpdViewsServicePathPrefix()}/v1/navigation/${type}/${id}/views?operation=getJpdNavigationViews`,
		).then((response) =>
			fg('polaris_global_views')
				? [
						...(response?.globalViews ?? []).map((view) => ({ ...view, global: true as const })),
						...(response.views ?? []),
					]
				: (response.views ?? []),
		);

const getProjectsViews = fetchViewsFactory<number>('projects');

/**
 * @param {number} id - Project's ID
 */
export const fetchProjectsViews = (projectId: number): Promise<NavigationView[]> => {
	// Check if we have early fetched data
	const cachedPromise = getEarlyFetchResponse(projectId);

	if (cachedPromise) {
		return wrapPromiseWithFetchInteraction(
			cachedPromise,
			'earlyScript/getJpdNavigationViews',
		).catch(() => {
			// If the cached promise fails, fall back to a regular fetch
			return getProjectsViews(projectId);
		});
	}

	// If no early fetched data is available, perform the fetch
	return getProjectsViews(projectId);
};

/**
 * @param {string} id - Collection's UUID
 */
export const fetchCollectionsViews: (id: string) => Promise<NavigationView[]> =
	fetchViewsFactory<string>('collections');
